// A pack's catalogue (structures.json + pack.json), indexed for the UI: search, the layer list,
// the inspector and the label candidates. The viewport loads the geometry itself.

import { CONNECTOME_CONTRACT, loadTissueCatalogue } from './tissue.ts';
import type { Activity } from './activity.ts';
import type { TissueTables } from './tissue.ts';

export type Provenance = 'measured' | 'derived' | 'reference' | 'simulated' | 'illustrative';

export interface Structure {
  id: string;
  name: string;
  /** What a callout calls it, at most 40 characters (asset-pack contract; design's table since #618; data/shortNames.ts). */
  short_name?: string;
  synonyms: string[];
  system: string;
  parent: string | null;
  /** Left, right, midline or bilateral (asset pack v0); `unspecified` (asset-pack body revision, #1429) claims no laterality, so the app shows no side. */
  hemisphere: 'L' | 'R' | 'midline' | 'bilateral' | 'unspecified' | null;
  provenance: Provenance;
  method?: string;
  confidence?: string;
  description?: string;
  description_source?: string;
  source?: { manifest_id?: string; citation?: string; licence?: string } & Record<string, unknown>;
  mesh: string | null;
  tract: string | null;
  labelmap?: string | null;
  label_value?: number | null;
  color?: string;
  region_color?: string;
  role?: 'context';
  display?: string;
  /** Asset-pack v0.3: another representation of these structures' anatomy (an HD cortex's hemisphere surface). */
  alternative_of?: string[];
  /** Other structures it relates to: a tract's `origin` and `target`. */
  related?: { id: string; role: string }[];
}

/** A pack layer's record in `pack.json` (connectome-pack-v0 `layers`): what it is and how it was made. */
export interface PackLayer {
  provenance: string;
  method?: string;
  confidence?: string;
  files?: string[];
}

export interface PackInfo {
  id: string;
  version: string;
  space: string;
  /** `connectome-pack-v0` for connectome and tissue packs; asset packs may leave it out. */
  contract?: string;
  /** Connectome packs: the specimen's species (`Homo sapiens`). */
  species?: string;
  subject?: unknown;
  notes?: unknown;
  /** Asset-pack v0.3: the packs this one composes with (the viewport loads them as one chain). */
  extends?: { id: string; version?: string }[];
  licence?: string;
  attribution?: { text?: string; title?: string; citation?: string; source_url?: string; licence?: string; licence_url?: string };
  /** Connectome packs: each layer's provenance and method, by layer id. */
  layers?: Record<string, PackLayer>;
  /** Connectome packs: the names behind the neuron table's small-integer columns (`layer`: L1…WM). */
  enums?: Record<string, string[]>;
  /** Tissue packs: the column the view centres on (H01's column.json summary). */
  column?: { centre_body?: number; x_mm?: number; y_mm?: number; radius_mm?: number; z_top_mm?: number; z_bottom_mm?: number };
  /** Where a pack in its own space sits in a brain space. */
  placement?: { space: string; affine?: number[][]; provenance?: string; method?: string };
}

export interface Catalogue {
  pack: PackInfo;
  /** As the viewport's `load` reply says: an anatomy pack, or a connectome or tissue pack (data/tissue.ts), or a run's own frame. */
  kind: 'anatomy' | 'connectome' | 'frame';
  structures: Structure[];
  byId: Map<string, Structure>;
  children: Map<string, Structure[]>;
  /** Structures the viewport can draw: a mesh or a tract of its own. */
  drawable: Structure[];
  /** Layer groups in display order, with their drawable members. */
  systems: SystemGroup[];
  /** A pack that composes (asset-pack v0.3 `extends`): it and the packs it extends, itself first, for lookups by pack. */
  chain?: string[];
  /** A connectome pack's activity runs: the one it plays by default and what Methods says about it. */
  activity?: Activity;
  /** A connectome pack's neuron table and cell types, kept for the inspector's cell fields (data/tissueCell.ts). */
  tissue?: TissueTables;
}

export interface SystemGroup {
  system: string;
  label: string;
  members: Structure[];
}

/** Display names and order for the layer list. Unknown systems sort last under their own name. */
export const SYSTEMS: [string, string][] = [
  ['scalp', 'Scalp'],
  ['skull', 'Skull'],
  ['cortex', 'Cortex'],
  ['white-matter', 'White matter'],
  ['tract', 'Tracts'],
  ['subcortical', 'Deep nuclei'],
  ['ventricle', 'Ventricles'],
  ['cerebellum', 'Cerebellum'],
  ['brainstem', 'Brainstem'],
  ['artery', 'Arteries'],
  ['vein', 'Veins'],
  ['sinus', 'Dural sinuses'],
  ['cranial-nerve', 'Cranial nerves'],
  // Connectome and tissue packs (data/tissue.ts).
  ['neurons', 'Cells'],
  ['neuropil', 'Neuropils'],
  ['shell', 'Tissue outline'],
];
const SYSTEM_LABEL = new Map(SYSTEMS);
const SYSTEM_ORDER = new Map(SYSTEMS.map(([s], i) => [s, i]));

export const systemLabel = (s: string) => SYSTEM_LABEL.get(s) ?? s.replace(/-/g, ' ').replace(/^./, (c) => c.toUpperCase());

/** Layer modes at load (interaction spec §3, as the wgpu engine's `default_layer_state`). The
 *  app sends every system's mode explicitly, so this table is the one source of truth. */
export const DEFAULT_MODES: Record<string, 'shown' | 'ghost' | 'hidden'> = {
  cortex: 'shown',
  cerebellum: 'shown',
  brainstem: 'shown',
  artery: 'shown',
  vein: 'shown',
  sinus: 'shown',
  'cranial-nerve': 'shown',
  tract: 'hidden',
  subcortical: 'hidden',
  ventricle: 'hidden',
  'white-matter': 'hidden',
  lymphatic: 'hidden',
  skull: 'ghost',
  scalp: 'ghost',
  meninges: 'ghost',
  // A tissue pack's outline: the sample reads as a window into tissue, never a box (micro-scale direction §4).
  shell: 'hidden',
};

/** The systems the app has a look for: the engine's original 15 tokens (the table above) and the connectome and tissue
 *  packs' own (SYSTEMS). Since #1422 the engine loads any valid `system`, and draws the ones without a look solid neutral grey
 *  (the head pack's muscle, eye, ear, gland, airway, spine and oral, 298 structures); until they have looks they start
 *  hidden, and the layer list still offers them (the lead's ruling for batch 74, #2 5988717298). */
const LOOKED: ReadonlySet<string> = new Set([...Object.keys(DEFAULT_MODES), ...SYSTEMS.map(([s]) => s)]);

/** A system's mode at load: the table's, else shown for a system the app has a look for, else hidden. */
export const defaultMode = (system: string): 'shown' | 'ghost' | 'hidden' => DEFAULT_MODES[system] ?? (LOOKED.has(system) ? 'shown' : 'hidden');

export const isContext = (s: Structure) => s.role === 'context' || s.display === 'context';

/** A system's swatch: design's structure colour for it (`--ba-anatomy-<system>`, tokens 1.3.0), else
 *  the given colour, for systems the design system doesn't name (a tissue pack's cells). */
export const systemSwatch = (system: string, fallback?: string) => `var(--ba-anatomy-${system}, ${fallback ?? '#999'})`;

export function index(pack: PackInfo, structures: Structure[], kind: Catalogue['kind'] = 'anatomy'): Catalogue {
  const byId = new Map(structures.map((s) => [s.id, s]));
  const children = new Map<string, Structure[]>();
  for (const s of structures) {
    if (!s.parent) continue;
    const list = children.get(s.parent) ?? [];
    list.push(s);
    children.set(s.parent, list);
  }
  const drawable = structures.filter((s) => s.mesh || s.tract);
  const groups = new Map<string, Structure[]>();
  for (const s of drawable) {
    if (isContext(s)) continue;
    const list = groups.get(s.system) ?? [];
    list.push(s);
    groups.set(s.system, list);
  }
  const systems = [...groups.entries()]
    .map(([system, members]) => ({ system, label: systemLabel(system), members: members.sort((a, b) => a.name.localeCompare(b.name)) }))
    .sort((a, b) => (SYSTEM_ORDER.get(a.system) ?? 99) - (SYSTEM_ORDER.get(b.system) ?? 99) || a.label.localeCompare(b.label));
  return { pack, kind, structures, byId, children, drawable, systems };
}

/** A system's label as this catalogue's layer list shows it (a tissue pack names its neuropils by what they are). */
export const groupLabel = (cat: Pick<Catalogue, 'systems'> | null | undefined, system: string) => cat?.systems.find((g) => g.system === system)?.label ?? systemLabel(system);

/** Every drawable structure under `id`, including itself. */
export function subtree(cat: Catalogue, id: string): Structure[] {
  const out: Structure[] = [];
  const walk = (s: Structure) => {
    if (s.mesh || s.tract) out.push(s);
    for (const c of cat.children.get(s.id) ?? []) walk(c);
  };
  const root = cat.byId.get(id);
  if (root) walk(root);
  return out;
}

/** The chain of ancestors, outermost first, without the structure itself. */
export function ancestors(cat: Catalogue, id: string): Structure[] {
  const out: Structure[] = [];
  let s = cat.byId.get(id);
  while (s?.parent) {
    const p = cat.byId.get(s.parent);
    if (!p) break;
    out.unshift(p);
    s = p;
  }
  return out;
}

async function readJson<T>(url: string, fetchFn: typeof fetch): Promise<T> {
  const res = await fetchFn(url);
  if (!res.ok) throw new Error(`${url}: HTTP ${res.status}`);
  return (await res.json()) as T;
}

async function packStructures(assets: string, id: string, fetchFn: typeof fetch): Promise<Structure[]> {
  const raw = await readJson<Structure[] | { structures: Structure[] }>(`${assets}packs/${id}/structures.json`, fetchFn);
  return (Array.isArray(raw) ? raw : raw.structures).map((s) => ({ ...s, synonyms: s.synonyms ?? [] }));
}

/**
 * A composing pack's structures with those of the packs it extends, bases first, each id once, as the
 * viewport loads them as one chain. Its own hemisphere surfaces (`alternative_of` the base's cortex) are
 * the brain the viewport shows (data/brain.ts), as BigBrain's are over v0, not structures to list or label:
 * the viewport reports the base's parcels under the pointer on them.
 */
async function chainStructures(assets: string, pack: PackInfo, fetchFn: typeof fetch, chain: string[]): Promise<Structure[]> {
  chain.push(pack.id);
  const out: Structure[] = [];
  for (const ref of pack.extends ?? []) {
    if (chain.includes(ref.id)) continue;
    out.push(...(await chainStructures(assets, await readJson<PackInfo>(`${assets}packs/${ref.id}/pack.json`, fetchFn), fetchFn, chain)));
  }
  const ids = new Set(out.map((s) => s.id));
  for (const s of await packStructures(assets, pack.id, fetchFn)) {
    if (ids.has(s.id) || (pack.extends?.length && Array.isArray(s.alternative_of))) continue;
    ids.add(s.id);
    out.push(s);
  }
  return out;
}

/** A run's own frame in a pack's place (the engine's frame scene, viewport-api `load { frame }`): its space, and no structures. */
export function frameCatalogue(id: string, space: string): Catalogue {
  return { pack: { id, version: '', space }, kind: 'frame', structures: [], byId: new Map(), children: new Map(), drawable: [], systems: [] };
}

/** A pack's catalogue: an asset pack's structures.json (with the packs it extends), or a connectome pack's own tables (data/tissue.ts). */
export async function loadCatalogue(assets: string, packId: string, fetchFn: typeof fetch = fetch): Promise<Catalogue> {
  const base = `${assets}packs/${packId}/`;
  const pack = await readJson<PackInfo>(`${base}pack.json`, fetchFn);
  if (pack.contract === CONNECTOME_CONTRACT) return loadTissueCatalogue(base, pack, fetchFn);
  if (!pack.extends?.length) return index(pack, await packStructures(assets, packId, fetchFn));
  const chain: string[] = [];
  const cat = index(pack, await chainStructures(assets, pack, fetchFn, chain));
  return { ...cat, chain };
}

// Midline and bilateral in the engine's own words (ui.rs `hemi_long`); packs write `midline` and `bilateral`, never `M`.
const HEMI: Record<string, string> = { L: 'Left', R: 'Right', midline: 'Midline', bilateral: 'Bilateral' };
export const hemisphereLabel = (h: Structure['hemisphere']) => (h ? HEMI[h] ?? null : null);
